import type { FollowerTemplate, PeculiarityEffect } from '../schemas';
import type { MoveRef, RecruitOutcome } from '../schemas/character';
import { countOccurrences, isPeculiarityEffectComplete } from './followerPeculiarities';

/**
 * **D8c — FI-41**: predicate di "setup completo" per un Follower in
 * costruzione. Estratto da `followerCreation.ts` per rispettare
 * `max-lines: 400` quando D8c ha aggiunto `hasUpgradesSetupComplete`.
 *
 * API pubblica esposta: {@link isFollowerSetupComplete},
 * {@link enrichGroupsWithMainShorthand}, {@link getPickCountForOutcome},
 * {@link getEffectivePickCount}. Le funzioni `has*` interne sono private.
 *
 * **Pattern SSOT**: questo modulo è l'unico punto in cui si decide se un
 * Follower è "pronto per essere costruito". `getFollowersFromDraft` lo
 * consulta prima di chiamare `buildFollowerFromTemplate`.
 */

/**
 * Predicate: il setup di una mossa-source con `followerTemplateId` è
 * completo (tutti i `ChoiceGroup` referenziati dal template hanno almeno
 * un valore selezionato, e il `nameFromNarrativeField` è valorizzato)?
 *
 * D8c — FI-41: se il PG possiede mosse di upgrade (`upgradeMoveRefs`), il
 * setup di ognuna deve essere completo (additionalAlwaysTraits +
 * peculiaritiesFromChoiceGroup).
 *
 * Usata da `getFollowersFromDraft` per filtrare le mosse in costruzione.
 * Non sostituisce `isMoveSetupComplete` del builder (che verifica
 * `pickCount` esatti su `MoveSchema.choiceGroups`); è un pre-controllo
 * light prima di chiamare `buildFollowerFromTemplate`.
 */
export function isFollowerSetupComplete(
  moveRef: MoveRef,
  template: FollowerTemplate,
  upgradeMoveRefs?: ReadonlyMap<string, MoveRef>,
): boolean {
  const selectedChoices = moveRef.selectedChoices ?? [];
  const groups = enrichGroupsWithMainShorthand(moveRef.selectedChoiceGroups, selectedChoices);
  const fields = moveRef.narrativeFieldValues ?? {};
  return (
    hasRecruitOutcomeIfRequired(moveRef, template) &&
    hasChoiceGroupSelection(groups, template.modifierBonus?.fromChoiceGroup) &&
    hasArchetypeSelectionWithCustom(groups, fields, template) &&
    hasTraitSelectionWithCustom(groups, fields, template) &&
    hasPeculiaritiesSetupComplete(groups, fields, template) &&
    hasNarrativeFieldFilled(fields, template.nameFromNarrativeField) &&
    // D8b: se il template richiede Abilità primaria da narrativeField (Braccio
    // Destro), il field deve essere valorizzato.
    hasNarrativeFieldFilled(fields, template.primarySkillFromNarrativeField?.narrativeFieldId) &&
    // D8b: se il template richiede una capacità utile sempre-presente da
    // narrativeField (Braccio Destro), il label è obbligatorio.
    hasNarrativeFieldFilled(fields, template.traitFromNarrativeFields?.labelFieldId) &&
    // D8c — FI-41: ogni mossa di upgrade posseduta dal PG deve avere il
    // proprio setup (choiceGroup + narrativeFields delle opzioni scelte +
    // narrativeFields delle additionalAlwaysTraits) completo.
    hasUpgradesSetupComplete(template, upgradeMoveRefs)
  );
}

/**
 * Per le mosse single-group (`choices` shorthand), il consumer può non
 * popolare `selectedChoiceGroups.main`. Esponiamo qui una vista unificata:
 * se il template referenzia `'main'` ma il gruppo non è in
 * `selectedChoiceGroups`, usiamo i `selectedChoices` come fallback.
 */
export function enrichGroupsWithMainShorthand(
  selectedChoiceGroups: Readonly<Record<string, readonly string[]>> | undefined,
  selectedChoices: readonly string[],
): Record<string, readonly string[]> {
  const groups: Record<string, readonly string[]> = { ...(selectedChoiceGroups ?? {}) };
  if (!('main' in groups) && selectedChoices.length > 0) {
    groups.main = selectedChoices;
  }
  return groups;
}

/**
 * D8a — pickCount effettivo del gruppo di peculiarità della mossa-source.
 *
 * Per mosse con `FollowerTemplate.recruitRoll` definito + `MoveRef.recruitOutcome`
 * impostato, il pickCount è derivato dall'esito:
 *  - `'10+'` → `recruitRoll.onSuccess.choicesCount`
 *  - `'7-9'` → `recruitRoll.onPartial.choicesCount`
 *
 * Per mosse senza `recruitRoll` (Compagno Animale, Famiglio, ecc.), ritorna
 * `undefined` — il pickCount sta sulla mossa-source come `MoveChoice.pickCount`
 * statico (gestito dalla logica builder esistente).
 *
 * Per mosse con `recruitRoll` ma senza `recruitOutcome` ancora dichiarato,
 * ritorna `undefined` — la UI deve mostrare il selettore di outcome prima di
 * permettere selezioni.
 */
export function getEffectivePickCount(
  template: FollowerTemplate,
  moveRef: MoveRef,
): number | undefined {
  if (template.recruitRoll === undefined) return undefined;
  return getPickCountForOutcome(template, moveRef.recruitOutcome);
}

/**
 * D8a — variante pura di `getEffectivePickCount` che accetta direttamente
 * un `RecruitOutcome` (utile per la UI che vuole pre-calcolare il pickCount
 * mentre l'utente sta ancora scegliendo l'outcome).
 */
export function getPickCountForOutcome(
  template: FollowerTemplate,
  outcome: RecruitOutcome | undefined,
): number | undefined {
  if (template.recruitRoll === undefined) return undefined;
  if (outcome === undefined) return undefined;
  return outcome === '10+'
    ? template.recruitRoll.onSuccess.choicesCount
    : template.recruitRoll.onPartial.choicesCount;
}

// ---------------------------------------------------------------------------
// Internals — has*
// ---------------------------------------------------------------------------

function hasRecruitOutcomeIfRequired(moveRef: MoveRef, template: FollowerTemplate): boolean {
  if (template.recruitRoll === undefined) return true;
  return moveRef.recruitOutcome !== undefined;
}

function hasChoiceGroupSelection(
  groups: Readonly<Record<string, readonly string[]>>,
  groupId: string | undefined,
): boolean {
  if (groupId === undefined) return true;
  const sel = groups[groupId];
  return sel !== undefined && sel.length > 0;
}

function hasNarrativeFieldFilled(
  fields: Readonly<Record<string, string>>,
  fieldId: string | undefined,
): boolean {
  if (fieldId === undefined) return true;
  return (fields[fieldId]?.trim().length ?? 0) > 0;
}

function hasArchetypeSelectionWithCustom(
  groups: Readonly<Record<string, readonly string[]>>,
  fields: Readonly<Record<string, string>>,
  template: FollowerTemplate,
): boolean {
  const groupId = template.archetypeFromChoiceGroup;
  if (groupId === undefined) return true;
  const sel = groups[groupId];
  if (sel === undefined || sel.length === 0) return false;
  const custom = template.archetypeCustomFromNarrativeField;
  if (custom?.fromChoiceGroup !== groupId) return true;
  if (!sel.includes(custom.whenChoice)) return true;
  return hasNarrativeFieldFilled(fields, custom.narrativeFieldId);
}

function hasTraitSelectionWithCustom(
  groups: Readonly<Record<string, readonly string[]>>,
  fields: Readonly<Record<string, string>>,
  template: FollowerTemplate,
): boolean {
  const mapping = template.traitsFromChoiceGroup;
  if (mapping === undefined) return true;
  const sel = groups[mapping.fromChoiceGroup];
  if (sel === undefined || sel.length === 0) return false;
  const custom = template.traitCustomFromNarrativeField;
  if (custom?.fromChoiceGroup !== mapping.fromChoiceGroup) return true;
  if (!sel.includes(custom.whenChoice)) return true;
  return hasNarrativeFieldFilled(fields, custom.labelFieldId);
}

/**
 * **FI-8b**: verifica che ogni peculiarità selezionata abbia eventuali
 * prompt narrativi (Abilità / stat / label custom) compilati. Per le
 * occorrenze duplicate di `custom-trait-from-narrative` (caso 2× capacità
 * utile), entrambi i set `labelFieldId(Second)` devono essere valorizzati.
 */
function hasPeculiaritiesSetupComplete(
  groups: Readonly<Record<string, readonly string[]>>,
  fields: Readonly<Record<string, string>>,
  template: FollowerTemplate,
): boolean {
  const mapping = template.peculiaritiesFromChoiceGroup;
  if (mapping === undefined) return true;
  const sel = groups[mapping.fromChoiceGroup];
  if (sel === undefined || sel.length === 0) return false;
  const counts = countOccurrences(sel);
  for (const [optionId, count] of counts) {
    const effect = mapping.optionToEffect[optionId];
    if (!effect) return false;
    if (!isPeculiarityEffectComplete(effect, count, fields)) return false;
  }
  return true;
}

/**
 * D8c — FI-41: predicate che verifica il setup di tutte le mosse di upgrade
 * applicabili. Estrae in helper sub-predicate per ridurre annidamento.
 */
function hasUpgradesSetupComplete(
  template: FollowerTemplate,
  upgradeMoveRefs: ReadonlyMap<string, MoveRef> | undefined,
): boolean {
  if (!upgradeMoveRefs || template.upgrades.length === 0) return true;
  for (const upgrade of template.upgrades) {
    const upgradeRef = upgradeMoveRefs.get(upgrade.fromMoveId);
    if (!upgradeRef) continue;
    if (!isSingleUpgradeSetupComplete(upgrade, upgradeRef)) return false;
  }
  return true;
}

function isSingleUpgradeSetupComplete(
  upgrade: FollowerTemplate['upgrades'][number],
  upgradeRef: MoveRef,
): boolean {
  const fields = upgradeRef.narrativeFieldValues ?? {};
  for (const traitConfig of upgrade.additionalAlwaysTraitsFromNarrativeFields) {
    if (!hasNarrativeFieldFilled(fields, traitConfig.labelFieldId)) return false;
  }
  const mapping = upgrade.peculiaritiesFromChoiceGroup;
  if (mapping === undefined) return true;
  const selected = upgradeRef.selectedChoiceGroups?.[mapping.fromChoiceGroup] ?? [];
  if (selected.length === 0) return false;
  return areAllSelectedEffectsComplete(selected, mapping.optionToEffect, fields);
}

function areAllSelectedEffectsComplete(
  selected: readonly string[],
  optionToEffect: Readonly<Record<string, readonly PeculiarityEffect[]>>,
  fields: Readonly<Record<string, string>>,
): boolean {
  for (const optionId of selected) {
    const effects = optionToEffect[optionId];
    if (!effects) return false;
    for (const effect of effects) {
      if (!isPeculiarityEffectComplete(effect, 1, fields)) return false;
    }
  }
  return true;
}
